import { useParentSize } from "@visx/responsive";
import { useTableScales } from "../growth/ProductsTable";
import Table from "../../topProductsTable/Table";
import { getFillColor } from "./utils";
import type { HierarchyNode } from "d3-hierarchy";
import type { ProductMetadatum } from "@atlas-int/data/types";
import { ColorBy } from "@atlas-int/data";
import type { ProcessedNode } from "./types";

interface OverlayTableProps {
  colorMap: Map<string, string>;
  metaDataLookup: Map<string, HierarchyNode<ProductMetadatum>>;
  displayData: Record<string, unknown>;
  totalExportLookup: Map<string, { exportValue?: number; exportValueGrowth5?: number; pci?: number }>;
  selection: string;
  linkLookup: Record<string, string[]>;
  nodeLookup: Map<string, ProcessedNode>;
  countryExportLookup: Map<string, { exportRca?: number; exportValue?: number; normalizedPci?: number; normalizedCog?: number; normalizedDistance?: number; productId?: string }>;
  highlight: (id: string | null) => void;
  setRingItem: (id: string) => void;
  colorBySelection: ColorBy;
  clusterColorLookup: Map<string, { cluster_col?: string }>;
  complexityColorScale: (value: number | undefined) => string;
  rcaThreshold: number;
  year: number | undefined;
  productClass: string;
}

const OverlayTable = ({
  colorMap,
  metaDataLookup,
  displayData,
  totalExportLookup,
  selection,
  linkLookup,
  nodeLookup,
  countryExportLookup,
  highlight,
  setRingItem,
  colorBySelection,
  clusterColorLookup,
  complexityColorScale,
  rcaThreshold,
  year,
  productClass,
}: OverlayTableProps) => {
  const { countryYearThresholds = [] } = displayData as { countryYearThresholds?: Parameters<typeof useTableScales>[0] };
  const { complexityScale, opportunityScale, feasibilityScale, rcaScale } =
    useTableScales(countryYearThresholds);
  const { parentRef, width, height } = useParentSize({ debounceTime: 150 });
  const fellowNodes = (linkLookup[selection] || [])
    .map((n: string) => nodeLookup.get(n))
    .filter((d): d is NonNullable<typeof d> => !!d);

  const tableData = fellowNodes
    .map((d) => ({ ...displayData, ...d, ...countryExportLookup.get(d.productId), productId: d.productId }) as Record<string, unknown> & { productId: string; normalizedPci: number; normalizedCog: number; normalizedDistance: number; exportRca: number })
    .map((d) => {
      const productData = metaDataLookup.get(d.productId as string)?.data;
      const topParentId = productData?.topParent?.productId;

      // Skip nodes without required properties
      if (!d || !productData) {
        return null;
      }

      const { fill } = getFillColor(
        d as Parameters<typeof getFillColor>[0],
        productData as Parameters<typeof getFillColor>[1],
        topParentId || "",
        colorBySelection,
        colorMap,
        clusterColorLookup,
        complexityColorScale,
        totalExportLookup,
        [],
        countryExportLookup,
        rcaThreshold,
      );

      return {
        ...d,
        id: d.productId,
        color: fill,
        code: metaDataLookup.get(d.productId)?.data?.code,
        name: metaDataLookup.get(d.productId)?.data?.nameShortEn,
        complexityDecile: complexityScale(d.normalizedPci),
        opportunityGainDecile: opportunityScale(d.normalizedCog),
        rcaDecile: rcaScale(d.exportRca),
        feasibilityDecile: feasibilityScale(d.normalizedDistance),
        globalExportValue: totalExportLookup.get(d.productId)?.exportValue || 0,
        normalizedPCI: d.normalizedPci,
        normalizedFeasibility: d.normalizedDistance,
        globalExportValueChange:
          totalExportLookup.get(d.productId)?.exportValueGrowth5 || 0,
        //balanced approach
        score:
          0.35 * d.normalizedCog +
          0.15 * d.normalizedPci +
          0.5 * d.normalizedDistance,
      };
    })
    .filter((d) => d && (productClass === "HS22" ? true : metaDataLookup.get(d.productId)?.data?.showFeasibility))
    .sort((d1, d2) => d2.score - d1.score);

  return (
    <div ref={parentRef} style={{ height: "100%", width: "100%" }}>
      <Table
        products={tableData as unknown as Parameters<typeof Table>[0]["products"]}
        getFluentString={() => "hello"}
        width={width || undefined}
        height={height || undefined}
        zIndexFromParent={20}
        year={year}
        condensed
        highlight={highlight}
        setRingItem={setRingItem}
      />
    </div>
  );
};
export default OverlayTable;
